/**
 * Search overlay.
 *
 * Debounced live search against `glunz/v1/search`. Results are rendered into a
 * list inside the overlay. When the overlay opens (via overlay.js), focus
 * moves to the input; when it closes, the input and results are cleared.
 * Stale requests are ignored via a request counter.
 */
import axios from 'axios'

const DEBOUNCE_MS = 250
const MIN_QUERY_LENGTH = 2
const OVERLAY_KEY = 'search'

const root = document.querySelector('[data-search]')
if (root) init(root)

function init(root) {
    const form = root.querySelector('[data-search-form]')
    const input = root.querySelector('[data-search-input]')
    const results = root.querySelector('[data-search-results]')
    const empty = root.querySelector('[data-search-empty]')
    const error = root.querySelector('[data-search-error]')
    const status = root.querySelector('[data-search-status]')
    if (!input || !results) return

    let debounceTimer = null
    let requestCounter = 0
    let lastQuery = ''

    const clearOutput = () => {
        results.innerHTML = ''
        empty.setAttribute('hidden', '')
        error.setAttribute('hidden', '')
        status.textContent = ''
    }

    const renderResults = (posts, query) => {
        results.innerHTML = ''
        error.setAttribute('hidden', '')

        if (posts.length === 0) {
            empty.removeAttribute('hidden')
            status.textContent = `Keine Ergebnisse für "${query}".`
            return
        }

        empty.setAttribute('hidden', '')
        const frag = document.createDocumentFragment()
        posts.forEach(post => frag.appendChild(buildResultItem(post)))
        results.appendChild(frag)
        status.textContent = `${posts.length} Ergebnisse für "${query}".`
    }

    const runSearch = async (query) => {
        const id = ++requestCounter
        try {
            const response = await axios.get(window.php_vars.restUrl + 'glunz/v1/search', {
                params: { query }
            })
            if (id !== requestCounter) return
            renderResults(response.data.posts || [], query)
        } catch (_err) {
          console.error('Search request failed', _err)
            if (id !== requestCounter) return
            results.innerHTML = ''
            empty.setAttribute('hidden', '')
            error.removeAttribute('hidden')
            status.textContent = 'Die Suche ist fehlgeschlagen.'
        }
    }

    // Results are live/debounced, so there is no server-side submit target — but
    // the form must still *be* submittable: iOS only dismisses the software
    // keyboard on implicit form submission, and it only offers one when the form
    // has a submit button (hence the visually-hidden one in the markup). So let
    // Enter submit, cancel the navigation, flush a still-pending debounce so the
    // results are on screen, then drop the keyboard.
    //
    // The blur is limited to touch devices: on desktop, Enter yanking focus out
    // of a live-search field would just make it harder to keep refining a query.
    form?.addEventListener('submit', (e) => {
        e.preventDefault()

        const query = input.value.trim()
        if (debounceTimer && query.length >= MIN_QUERY_LENGTH) {
            clearTimeout(debounceTimer)
            debounceTimer = null
            runSearch(query)
        }

        if (window.matchMedia('(pointer: coarse)').matches) input.blur()
    })

    input.addEventListener('input', () => {
        clearTimeout(debounceTimer)
        const query = input.value.trim()

        if (query === lastQuery) return
        lastQuery = query

        if (query.length < MIN_QUERY_LENGTH) {
            requestCounter++ // invalidate any in-flight request
            clearOutput()
            return
        }

        debounceTimer = setTimeout(() => {
            debounceTimer = null
            runSearch(query)
        }, DEBOUNCE_MS)
    })

    window.addEventListener('overlay:opened', (e) => {
        if (e.detail && e.detail.key === OVERLAY_KEY) {
            setTimeout(() => input.focus(), 50)
        }
    })

    window.addEventListener('overlay:closed', (e) => {
        if (e.detail && e.detail.key === OVERLAY_KEY) {
            clearTimeout(debounceTimer)
            requestCounter++
            input.value = ''
            lastQuery = ''
            clearOutput()
        }
    })
}

function buildResultItem(post) {
    const li = document.createElement('li')
    li.className = 'search-result'


    li.innerHTML = post.html
    return li
}
